import { z } from 'zod';
import { memberIdSchema } from '../identity/legacy-identity-id';
import { apiEnvelope } from '../envelope';
import {
  ACCESS_JUSTIFICATION_REASON_OTHER,
  p2AccessJustificationReasonSchema,
} from '../care/access-justification';

/**
 * Wire contract for the `member_access_grants` audit read — "who accessed this
 * member, when, and why".
 *
 * Three properties are structural rather than incidental:
 *
 *   - **The item is HAND-AUTHORED, and its ADR-066 Rule 3 drift guard lives in
 *     `response.drift.test.ts` beside this module** — the same split
 *     `staff-notifications/schema.ts` uses. Deriving the shipped schema through
 *     `rowResponse` here would make this module import `../db/schema`, and this
 *     module is exported from the package barrel: every consumer of
 *     `@galapagos/shared`, mobile and web included, would then pull the whole
 *     Drizzle table graph and `pg-core` into its bundle, because the table is a
 *     live value consumed at module-evaluation time and cannot be dropped. The
 *     guard gets the derivation instead, where it costs nothing at runtime and
 *     still fails on a column that moves.
 *
 *   - **The denormalized accessor and member snapshots are served as stored.**
 *     `internal_user_name`, `internal_user_email`, `internal_user_role` and
 *     `member_name` are copied onto the row at write time precisely so the audit
 *     can say who the accessor WAS at the time, and so it survives a
 *     soft-deleted accessor. Re-joining to the live rows would answer a
 *     different question than the one an audit asks.
 *
 *   - **Strictness is split by direction** (ADR-135): `.strict()` on the
 *     request, so the server refuses input it does not understand, and NOT on
 *     the response, so an installed client tolerates a field a later server
 *     adds. A strict response schema does not stop a server sending a new key;
 *     it only breaks the client on the next additive rollout.
 *
 * The request schema describes the RAW query string — every field is a string,
 * because that is what a query parameter is. Parsing `limit` to a number and the
 * instants to Luxon values is the service's job, and naming the raw shape here
 * keeps the handler's own parse honest about what it receives.
 *
 * This module is schemas only. The `registerApiRoute` contract that carries them
 * lives under `apps/api/src/internal-access-grants/runtime` (ADR-061 Rule 1).
 */

/** Page size when the caller names none. */
export const ACCESS_GRANT_AUDIT_PAGE_SIZE_DEFAULT = 50;
/** Hard ceiling on a single audit page. */
export const ACCESS_GRANT_AUDIT_PAGE_SIZE_MAX = 100;
/**
 * Hard ceiling on an inbound cursor.
 *
 * The cursor is the one free-form caller-controlled string this surface takes —
 * every other field is a uuid, an instant, or a bounded number. A server-issued
 * keyset position over `(accessed_at, id)` needs a fraction of this, so the
 * bound costs a caller nothing and keeps an arbitrarily long value from reaching
 * the decoder at all.
 */
export const ACCESS_GRANT_AUDIT_CURSOR_MAX_LENGTH = 512;

/**
 * The instant, as the repo projects it: a full-microsecond ISO-8601 string.
 *
 * The six fractional digits are ASSERTED, not merely described. This is the
 * value the keyset cursor round-trips, and a millisecond-truncated projection
 * makes the `=` half of the seek stop matching, which silently disables the
 * `id` tiebreaker at a page edge. A bare `z.string()` here would accept the
 * truncated form, so the contract would document a precision nothing enforced —
 * and the drift guard that claims to pin it would pass on any string at all.
 */
const accessedAtSchema = z
  .string()
  .regex(
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z$/,
    'accessedAt must be a full-microsecond UTC ISO-8601 instant',
  );

/**
 * One audit row as the read serves it.
 *
 * `reason_detail` is served in full and is deliberately not truncated: it is the
 * free text a staff member wrote to justify reaching a member, and an audit that
 * abbreviates the justification has not reported it.
 */
export const accessGrantAuditItemSchema = z.object({
  id: z.string(),
  internalUserId: z.string(),
  internalUserName: z.string().nullable(),
  internalUserEmail: z.string(),
  internalUserRole: z.string(),
  memberId: z.string(),
  memberName: z.string().nullable(),
  reasonCode: z.string(),
  reasonLabel: z.string(),
  reasonDetail: z.string().nullable(),
  accessedAt: accessedAtSchema,
});
export type AccessGrantAuditItem = z.infer<typeof accessGrantAuditItemSchema>;

/**
 * A keyset page, newest first.
 *
 * `nextCursor` is explicitly `null` on the last page rather than absent, so a
 * client cannot read a truncated body as "there is more" — the same contract the
 * repo's other keyset pages carry.
 */
export const accessGrantAuditPageSchema = z.object({
  items: z.array(accessGrantAuditItemSchema),
  nextCursor: z.string().nullable(),
});
export type AccessGrantAuditPage = z.infer<typeof accessGrantAuditPageSchema>;

export const accessGrantAuditListResponseSchema = apiEnvelope(accessGrantAuditPageSchema);

/**
 * The raw query string this endpoint accepts.
 *
 * `.strict()` per ADR-135 Rule 1 — which is one rule covering both directions:
 * strict is for request/input schemas (the server refusing unexpected input) and
 * wrong on a client-parsed response. The server refuses a parameter it does not
 * understand rather than ignoring it, because an ignored filter is the failure
 * mode that matters here: a caller who believes they scoped a query to one
 * member and silently did not has read the whole log.
 *
 * NOTE for implementers: `registerApiRoute` renders this into the OpenAPI
 * document and does NOT validate against it at runtime. The handler parses
 * `c.req.query()` through this schema itself; without that, `.strict()` here
 * documents a rejection that never happens.
 */
export const accessGrantAuditListRequestSchema = z
  .object({
    memberId: z.string().optional(),
    internalUserId: z.string().optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    limit: z.string().optional(),
    cursor: z.string().max(ACCESS_GRANT_AUDIT_CURSOR_MAX_LENGTH).optional(),
  })
  .strict();
export type AccessGrantAuditListRequest = z.infer<typeof accessGrantAuditListRequestSchema>;

/**
 * The filter axes, as a closed vocabulary.
 *
 * Exported because three places must agree on it and would otherwise each hold
 * their own copy: the "at least one filter" rule, the canonical `filters`
 * telemetry label, and the bounded-cardinality claim the metric-tag allowlist
 * rests on. Sorted here, once, so the label has exactly one spelling per filter
 * shape.
 */
export const ACCESS_GRANT_AUDIT_FILTER_AXES = [
  'from',
  'internal_user_id',
  'member_id',
  'to',
] as const;
export type AccessGrantAuditFilterAxis = (typeof ACCESS_GRANT_AUDIT_FILTER_AXES)[number];

/**
 * Wire contract for recording ONE justification — the write half of this
 * resource, and the way a care person who is not on a member's care team
 * proceeds at all.
 *
 * Four fields, and what is ABSENT from them is the security property. The
 * accessor (`internal_user_*`) and the member's name are NOT accepted here:
 * the accessor comes from the proven staff identity and the member's name from
 * the database, because a row whose subject the audited party chose is not an
 * audit. `.strict()` is what turns that from a convention into a refusal — a
 * body carrying `internalUserId` or `memberName` is rejected rather than
 * ignored, so a client cannot believe it named the subject and silently not.
 *
 * `reasonLabel` is the one caller-authored string, and it is deliberately not
 * the audit's answer to "why". It is a snapshot of the copy the accessor was
 * SHOWN when they chose, rendered by the client from its own locale catalog
 * (ADR-017) — a server-side re-resolution would negotiate a locale of its own
 * and could store a string the accessor never read. The audited fact is
 * `reasonCode`: closed, validated here and again by the table's vocabulary
 * CHECK, and the thing a `GROUP BY` can answer.
 */

/**
 * Length bounds, mirroring `member_access_grants`' CHECKs exactly.
 *
 * Named rather than inlined so the schema and the table cannot drift apart
 * quietly: a value the schema admits and the CHECK refuses is a 500 where the
 * caller is owed a 400, and the failure surfaces in Postgres rather than here.
 */
export const ACCESS_GRANT_REASON_LABEL_MAX_LENGTH = 200;
export const ACCESS_GRANT_REASON_DETAIL_MAX_LENGTH = 2000;
/** Comfortably above a UUID; a backend2 sub has no fixed shape to pin tighter. */
export const MEMBER_REF_MAX_LENGTH = 128;

export const accessGrantCreateRequestSchema = z
  .object({
    /**
     * A Galapagos `members.id`, and nothing else. One identifier space, matching
     * both the table's foreign key and the audit read's `memberId` filter axis.
     * A caller holding a Pelago-1 id resolves it through the identity lookup
     * endpoint first; widening this field would let the two surfaces disagree
     * about which member an audit row names.
     *
     * The grammar is the shared one rather than `.uuid()`: a member imported
     * from backend2 keeps its `users.id`, an 8-4-4-4-12 hex GUID carrying no
     * RFC 4122 version or variant guarantee, so a strict validator here would
     * 400 exactly the members whose access most needs an audit row. Same
     * identifier space, same column type; only the false rejection is gone.
     */
    memberId: memberIdSchema.optional(),
    /**
     * The member as the caller addresses it: a Galapagos `members.id` OR a
     * backend2 `legacy_backend2_sub`, resolved server-side by the same resolver
     * the access decision uses, so the two cannot disagree about which member a
     * reference names. The row still records the resolved `members.id`.
     *
     * The alternative to `memberId` exists for a caller that holds only the
     * backend2 sub — a Pelago 1 view of a linked member — and may not learn the
     * Galapagos id before access is granted. Exactly one of the two is accepted.
     * A plain bounded string, because a backend2 sub carries no UUID guarantee.
     */
    memberRef: z.string().trim().min(1).max(MEMBER_REF_MAX_LENGTH).optional(),
    // The Galapagos vocabulary, not the Pelago 1 one: this endpoint writes
    // `member_access_grants`, whose CHECK is derived from the same list, so the
    // two refuse exactly the same codes and a refusal is a 400, never a 500.
    reasonCode: p2AccessJustificationReasonSchema,
    reasonLabel: z.string().trim().min(1).max(ACCESS_GRANT_REASON_LABEL_MAX_LENGTH),
    /**
     * Optional, and `''` is REFUSED rather than coerced to absent. The table
     * refuses it for the same reason: quietly turning an empty string into NULL
     * would let "I typed nothing" be recorded as "I was not asked".
     *
     * `.trim()` runs BEFORE `.min(1)`, so `'   '` is refused on the same
     * grounds — a justification of three spaces is the same non-answer as an
     * empty one, and the table's `length >= 1` CHECK would happily store it.
     * Trimming also normalises what is stored, which is what keeps this field
     * consistent with the member and accessor names: those pass through
     * `presentOrNull` and are trimmed, so without this the asymmetry would sit
     * inside a single audit row.
     */
    reasonDetail: z.string().trim().min(1).max(ACCESS_GRANT_REASON_DETAIL_MAX_LENGTH).optional(),
  })
  .strict()
  // `superRefine` rather than `refine` for the ISSUE it can raise, not for the
  // type it returns: it attaches the failure to `reasonDetail` via `path`, so a
  // client is told which field is missing instead of being handed a whole-object
  // refusal. Both forms keep the object shape readable by the OpenAPI generator
  // under the installed Zod, so that is NOT the reason to prefer one here.
  .superRefine((value, ctx) => {
    if ((value.memberId === undefined) === (value.memberRef === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['memberRef'],
        message: 'exactly one of memberId or memberRef is required',
      });
    }
    // Branching on the exported constant, never the `'other'` literal: the rule
    // travels with the vocabulary, so renaming the code breaks this line instead
    // of silently leaving the one reason that needs free text unguarded.
    if (
      value.reasonCode === ACCESS_JUSTIFICATION_REASON_OTHER &&
      value.reasonDetail === undefined
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reasonDetail'],
        message: `reasonDetail is required when reasonCode is "${ACCESS_JUSTIFICATION_REASON_OTHER}"`,
      });
    }
  });
export type AccessGrantCreateRequest = z.infer<typeof accessGrantCreateRequestSchema>;

/**
 * What a recorded justification answers with.
 *
 * `grantId` rather than Pelago 1's `auditId`, and a uuid rather than its
 * auto-increment integer: the id names a grant in `member_access_grants`.
 * `grantedUntil` keeps its Pelago 1 name verbatim — it is the field a client
 * reads to know how long it may proceed, and renaming it would break the one
 * caller the migration is meant to be transparent to.
 */
export const accessGrantCreatedSchema = z.object({
  /**
   * Constrained, though the envelope around it deliberately is not. ADR-135's
   * non-strict response is about tolerating fields a later server ADDS; it says
   * nothing about leaving a declared field's own format unpinned. This is the
   * value a client round-trips back into the audit read's member filter axis.
   * A grant id is server-minted, so it is genuinely RFC 4122 and stays
   * `.uuid()`; the member id it names is not, and uses the shared grammar
   * instead. The two are deliberately NOT described the same way — an imported
   * member keeps a GUID with no version or variant guarantee, and pinning both
   * ends to one shape is what 400ed those members in the first place.
   */
  grantId: z.string().uuid(),
  /** `accessed_at + the configured TTL`, as a UTC ISO-8601 instant. */
  grantedUntil: z.string(),
});
export type AccessGrantCreated = z.infer<typeof accessGrantCreatedSchema>;

/**
 * NOT `.strict()`, and that asymmetry with the request above is ADR-135 Rule 1
 * rather than an oversight: strict belongs on input, where the server refuses
 * what it does not understand, and is wrong on a client-parsed response, where
 * it cannot stop a server sending a new key and only breaks the installed
 * client on the next additive rollout.
 */
export const accessGrantCreateResponseSchema = apiEnvelope(accessGrantCreatedSchema);

/**
 * Wire contract for the access-grant VOLUME read — "who has recorded the most
 * justified accesses over this window, and across how many members".
 *
 * The answer is a count per accessor, and the shape is deliberately narrower
 * than the audit item above: no member id, no member name, no reason, no
 * detail. Those are what the audit read is for, behind the same tier; this read
 * answers "how often" so an outlier is visible without opening the rows, and a
 * response that carried any of them would turn a volume summary into a second
 * route to the who-reached-whom mapping.
 *
 * Strictness is split by direction exactly as for the audit read (ADR-135), and
 * the request describes the raw query string for the same reason.
 */

/** Rows returned when the caller names no `limit`. */
export const ACCESS_GRANT_VOLUME_LIMIT_DEFAULT = 50;
/** Hard ceiling on rows in a single volume read. */
export const ACCESS_GRANT_VOLUME_LIMIT_MAX = 100;
/** The window when the caller names no `from`, measured back from `to`. */
export const ACCESS_GRANT_VOLUME_WINDOW_DEFAULT_HOURS = 24;
/**
 * The widest window one read may span. The aggregation scans every grant in the
 * window — no index on `member_access_grants` leads with `accessed_at`, so a
 * window-only predicate cannot seek — so the bound keeps one request proportionate;
 * a longer question is two reads.
 */
export const ACCESS_GRANT_VOLUME_SPAN_MAX_DAYS = 31;

export const accessGrantVolumeRequestSchema = z
  .object({
    from: z.string().optional(),
    to: z.string().optional(),
    limit: z.string().optional(),
  })
  .strict();
export type AccessGrantVolumeRequest = z.infer<typeof accessGrantVolumeRequestSchema>;

/**
 * One accessor's volume over the window.
 *
 * Name and email are the snapshot on the accessor's most recent grant inside
 * the window — what the rows themselves recorded, not a re-join to the live
 * staff row, for the same reason the audit item serves its snapshots as stored.
 */
export const accessGrantVolumeItemSchema = z.object({
  internalUserId: z.string(),
  internalUserName: z.string().nullable(),
  internalUserEmail: z.string(),
  grantCount: z.number().int().min(1),
  distinctMemberCount: z.number().int().min(1),
  firstAccessedAt: accessedAtSchema,
  lastAccessedAt: accessedAtSchema,
});
export type AccessGrantVolumeItem = z.infer<typeof accessGrantVolumeItemSchema>;

/** The highest counts first, with the window the server actually applied. */
export const accessGrantVolumeSchema = z.object({
  items: z.array(accessGrantVolumeItemSchema),
  from: z.string(),
  to: z.string(),
});
export type AccessGrantVolume = z.infer<typeof accessGrantVolumeSchema>;

export const accessGrantVolumeResponseSchema = apiEnvelope(accessGrantVolumeSchema);
